import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, HOME_ROUTE } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { PageLoader } from "@/components/ui-kit";

import Login from "@/pages/Login";
import StudentHome from "@/pages/student/StudentHome";
import LearnHub from "@/pages/student/LearnHub";
import SubjectPage from "@/pages/student/SubjectPage";
import TopicPage from "@/pages/student/TopicPage";
import AskAIPage from "@/pages/student/AskAIPage";
import ProgressPage from "@/pages/student/ProgressPage";
import ExamRadarPage from "@/pages/student/ExamRadarPage";
import VideosPage from "@/pages/student/VideosPage";
import NotificationsPage from "@/pages/NotificationsPage";
import TeacherDashboard from "@/pages/teacher/TeacherDashboard";
import AIStudio from "@/pages/teacher/AIStudio";
import QuestionBank from "@/pages/teacher/QuestionBank";
import StudentsPage from "@/pages/teacher/StudentsPage";
import StudentProfile from "@/pages/teacher/StudentProfile";
import CurriculumPage from "@/pages/teacher/CurriculumPage";
import SchoolDashboard from "@/pages/school/SchoolDashboard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ContentManager from "@/pages/admin/ContentManager";
import ParentDashboard from "@/pages/parent/ParentDashboard";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={HOME_ROUTE[user.role] || "/app"} replace />;
  return <AppShell>{children}</AppShell>;
}

function DefaultRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={HOME_ROUTE[user.role] || "/app"} replace />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<DefaultRedirect />} />
      {/* Student */}
      <Route path="/app" element={<Protected roles={["student"]}><StudentHome /></Protected>} />
      <Route path="/learn" element={<Protected roles={["student"]}><LearnHub /></Protected>} />
      <Route path="/learn/subject/:id" element={<Protected roles={["student"]}><SubjectPage /></Protected>} />
      <Route path="/topic/:id" element={<Protected roles={["student"]}><TopicPage /></Protected>} />
      <Route path="/ask-ai" element={<Protected roles={["student"]}><AskAIPage /></Protected>} />
      <Route path="/progress" element={<Protected roles={["student"]}><ProgressPage /></Protected>} />
      <Route path="/exam-radar" element={<Protected roles={["student"]}><ExamRadarPage /></Protected>} />
      <Route path="/videos" element={<Protected roles={["student"]}><VideosPage /></Protected>} />
      <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
      {/* Teacher / Admin shared */}
      <Route path="/teacher" element={<Protected roles={["teacher", "independent_educator"]}><TeacherDashboard /></Protected>} />
      <Route path="/teacher/studio" element={<Protected roles={["teacher", "independent_educator", "super_admin", "school_admin"]}><AIStudio /></Protected>} />
      <Route path="/teacher/question-bank" element={<Protected roles={["teacher", "super_admin", "school_admin"]}><QuestionBank /></Protected>} />
      <Route path="/teacher/students" element={<Protected roles={["teacher", "school_admin", "super_admin"]}><StudentsPage /></Protected>} />
      <Route path="/teacher/student/:id" element={<Protected roles={["teacher", "school_admin", "super_admin"]}><StudentProfile /></Protected>} />
      <Route path="/teacher/curriculum" element={<Protected roles={["teacher", "school_admin", "super_admin"]}><CurriculumPage /></Protected>} />
      <Route path="/content" element={<Protected roles={["teacher", "school_admin", "super_admin"]}><ContentManager /></Protected>} />
      {/* School / Admin / Parent */}
      <Route path="/school" element={<Protected roles={["school_admin", "super_admin"]}><SchoolDashboard /></Protected>} />
      <Route path="/admin" element={<Protected roles={["super_admin"]}><AdminDashboard /></Protected>} />
      <Route path="/parent" element={<Protected roles={["parent"]}><ParentDashboard /></Protected>} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
